import type { User } from '@sentry/react'
import { isExternalEvent } from './utils'

/**
 * Sentry is loaded lazily so it stays out of the critical path, which leaves a
 * window at startup with no global handlers — exactly where the errors that
 * break the boot live. Collect them meanwhile and replay once the chunk lands.
 */
const earlyErrors: unknown[] = []

const collectEarlyError = (event: ErrorEvent | PromiseRejectionEvent) => {
  earlyErrors.push('reason' in event ? event.reason : event.error)
}

window.addEventListener('error', collectEarlyError)
window.addEventListener('unhandledrejection', collectEarlyError)

/**
 * Must stay private: its type is the whole `@sentry/react` namespace, which
 * crashes TypeDoc when exported. Expose narrow wrappers instead.
 */
const sentryPromise = import('@sentry/react')

sentryPromise.then(Sentry => {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_URL,
    sendDefaultPii: true,
    beforeSend: event => {
      if (isExternalEvent(event)) {
        event.tags = { ...event.tags, source: 'external' }
      }
      return event
    }
  })
  window.removeEventListener('error', collectEarlyError)
  window.removeEventListener('unhandledrejection', collectEarlyError)
  earlyErrors.forEach(error => Sentry.captureException(error))
  earlyErrors.length = 0
})

/**
 * Reports an error without making the caller deal with the lazy import.
 */
export const captureException = (error: unknown) => {
  sentryPromise.then(Sentry => {
    Sentry.captureException(error)
  })
}

export const setTag = (key: string, value: string) => {
  sentryPromise.then(Sentry => {
    Sentry.setTag(key, value)
  })
}

export const setUser = (user: User | null) => {
  sentryPromise.then(Sentry => {
    Sentry.setUser(user)
  })
}
